import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import './UserNewForm.css';
import AuthContext from './AuthContext';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_PROXY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const NewUserForm = () => {
  const navigate = useNavigate();
  const { dataUser } = useOutletContext();
  const { customerId } = useParams();
  const { userId } = useParams();
  const { user } = useContext(AuthContext);
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [usernameChecking, setUsernameChecking] = useState(false);
  // WEST-794: email is required and must be unique across all users
  const [emailAvailable, setEmailAvailable] = useState(true);
  const [emailChecking, setEmailChecking] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    person_Name: '',
    email: '',
    login: '',
    password: '',
    user_role: 3,
    active: 'Yes',
    customer_id: ''
  });

  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    number: false,
    uppercase: false,
    symbol: false,
    noSpaces: true,
  });

  const [customers, setCustomers] = useState([]);

  useEffect(() => {
      // if no user in context, log them in again
      if (!user) {
        navigate('/login');
        return;
      }
      // check expiration
      const currentTime = Date.now() / 1000; // in seconds
      if (user.exp && user.exp <= currentTime) {
        // token has expired
        navigate('/login');
      }
    }, [user, navigate]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
            const response = await apiClient.post('/api/user/getActiveCustomers',
            {
              user_id: user?.id, // Add user_id to the body
              customer_id: user?.customer_id, // Add customer_id to the body
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`, // Authorization in headers
              }
            }
          );
          if (response && response.data) {
            setCustomers(response.data);
          }
        } catch (err) {
          console.error('Error loading customers:', err);
        }
    };

    if (user?.role_name === 'SuperAdmin') {
      fetchCustomers();
    }
  }, [user]);

  const checkUsernameAvailability = async (login) => {
    if (!login) {
      setUsernameAvailable(true);
      return;
    }
  
    setUsernameChecking(true);
  
    try {
      const response = await apiClient.get(`/api/user/checkUsername/${login}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.data && response.data.available) {
        setUsernameAvailable(true);
      } else {
        setUsernameAvailable(false);
      }
    } catch (error) {
      console.error("Error checking username:", error);
      setUsernameAvailable(false);
    } finally {
      setUsernameChecking(false);
    }
  };
  
  const checkEmailAvailability = async (email) => {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) {
      setEmailAvailable(true);
      return;
    }

    setEmailChecking(true);

    try {
      const response = await apiClient.get('/api/user/checkEmail', {
        params: { email: normalized },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setEmailAvailable(Boolean(response.data && response.data.available));
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailAvailable(false);
    } finally {
      setEmailChecking(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Handle specific cases for 'login', 'email' and 'password'
    if (name === 'login') {
      checkUsernameAvailability(value);
    } else if (name === 'email') {
      setEmailAvailable(true); // re-checked on blur
    } else if (name === 'password') {
      validatePassword(value);
    }
  };

  // Check the email once the user leaves the field rather than on every keystroke.
  const handleEmailBlur = (e) => {
    const normalized = (e.target.value || '').trim().toLowerCase();
    setFormData((prevData) => ({ ...prevData, email: normalized }));
    checkEmailAvailability(normalized);
  };
  
  const [error, setError] = useState(null);

  useEffect(() => {
    setFormData({
      title: '',
      person_Name: '',
      email: '',
      login: '',
      password: '',
      user_role: 3,
      active: 'Yes',
      customer_id: ''
    });
  }, [userId, dataUser]);

  const validatePassword = (password) => {
    const length = password.length >= 10;
    const number = /\d/.test(password);
    const uppercase = /[A-Z]/.test(password);
    const symbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const noSpaces = !/\s/.test(password);

    setPasswordValidation({ length, number, uppercase, symbol, noSpaces });
  };  

  const isPasswordValid = Object.values(passwordValidation).every((isValid) => isValid);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      setError('Please fix the password validation errors.');
      return;
    }
    if (user?.role_name === 'SuperAdmin' && !formData.customer_id) {
      setError('Please select a client.');
      return;
    }
    const email = (formData.email || '').trim().toLowerCase();
    if (!email) {
      setError('Email address is required.');
      return;
    }
    if (!emailAvailable) {
      setError('That email address is already in use.');
      return;
    }

    try {
      const payload = {
        ...formData,
        email,
        login: (formData.login || '').trim(),
        user_role: String(formData.user_role), // Convert user_role to a string
        customer_id: user?.role_name === 'SuperAdmin' 
          ? String(formData.customer_id) 
          : String(user.customer_id),
        id: null 
      };
      const response = await apiClient.post('/api/user/create',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const result = response && response.data && response.data.result;
      // The API reports problems as result.error (and some as an "ERROR ..." message).
      const problem = result && (result.error || (result.message && result.message.includes('ERROR') ? result.message : null));

      if (problem) {
        if (problem.includes('Email already in use') || (problem.includes('Duplicate') && problem.toLowerCase().includes('email'))) {
          setEmailAvailable(false);
          setError('That email address is already in use.');
        } else if (problem.includes('Email is required') || problem.includes('Invalid Email Format')) {
          setError('Please enter a valid email address.');
        } else if (problem.includes('Duplicate')) {
          setUsernameAvailable(false);
          setError('That username is unavailable.');
        } else {
          setError(problem);
        }
        return;
      }

      const url = `/customer/${customerId}/user/list`;
      window.open(url, '_self');

    } catch (error) {
      console.error('Error submitting form:', error);
      setError('An error occurred while submitting the form.');
    } 
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit} className="user-form">
      {error && <div className="error">{error}</div>}
      {!usernameAvailable && <div className="error">Username is already taken.</div>}
      {!emailAvailable && <div className="error">That email address is already in use.</div>}
      <table className="tableMain" border="0" width="100%" cellPadding="2" cellSpacing="2">
        <tbody>
		<tr>
            <td className="lbltxt">Title:</td>
            <td>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="inputFull fieldsMed"
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Person Name:</td>
            <td>
              <input
                type="text"
                name="person_Name"
                value={formData.person_Name}
                onChange={handleInputChange}
                className="inputFull fieldsMed"
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Email:</td>
            <td>
              <input
                type="email"
                name="email"
                required
                autoComplete="off"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                className="inputFull fieldsMed"
              />
              {emailChecking && <p className="info">Checking email...</p>}
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Login:</td>
            <td>
              <input
                type="text" 
                name="login"
                autoComplete="off"
                readOnly={true}
                onFocus={(e) => e.target.removeAttribute('readOnly')} // Remove readOnly on focus
                value={formData.login}
                onChange={handleInputChange}
                className="inputFull fieldsSm"
              />
            </td>
          </tr>
          <tr>
            <td className="lbltxt">Password:</td>
            <td>
              <input
                type="password" 
                name="password"
                autoComplete="off"
                readOnly={true}
                onFocus={(e) => e.target.removeAttribute('readOnly')} // Remove readOnly on focus
                value={formData.password}
                onChange={handleInputChange}
                className="inputFull fieldsMed"
              />
              <div className="password-validation">
                <ul>
                  <li className={passwordValidation.length ? 'valid' : 'invalid'}>
                    At least 10 characters
                  </li>
                  <li className={passwordValidation.number ? 'valid' : 'invalid'}>
                    At least 1 number
                  </li>
                  <li className={passwordValidation.uppercase ? 'valid' : 'invalid'}>
                    At least 1 uppercase letter
                  </li>
                  <li className={passwordValidation.symbol ? 'valid' : 'invalid'}>
                    At least 1 symbol (!@#$%^&*)
                  </li>
                  <li className={passwordValidation.noSpaces ? 'valid' : 'invalid'}>
                    No spaces
                  </li>
                </ul>
              </div>
            </td>
          </tr>
		  <tr>
            <td className="lbltxt">User Role:</td>
            <td>
                <select
                  name="user_role"
                  value={formData.user_role}
                  onChange={handleInputChange}
                  className="inputFull fieldsSm"
                >
                  { (user?.role_name === "Admin" || user?.role_name === "SuperAdmin") && <option value="3">User</option>}
                  { (user?.role_name === "Admin" || user?.role_name === "SuperAdmin")  && <option value="2">Customer Admin</option>}
                  { (user?.role_name === "SuperAdmin")  && <option value="1">Super Admin</option>}
                </select>
            </td>
          </tr>
          {user?.role_name === 'SuperAdmin' && (
            <tr>
              <td className="lbltxt">Client:</td>
              <td>
                <select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleInputChange}
                  className="inputFull fieldsMed"
                >
                  <option value="">Select a client</option>
                  {customers.map((cust) => (
                    <option key={cust.customer_number} value={cust.customer_number}>
                      {cust.name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          )}
          <tr>
            <td className="lbltxt">Active:</td>
            <td>
                <select
                  name="active"
                  value={formData.active}
                  onChange={handleInputChange}
                  className="inputFull fieldsSm"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              {(user?.role_name === "Admin" || user?.role_name === "SuperAdmin") ? (
                <>
                  <button type="submit" disabled={!usernameAvailable || !emailAvailable || emailChecking || !isPasswordValid} className="fieldsMed">
                    Create
                  </button>                  
                  {usernameChecking && <p className="info">Checking username...</p>}
                </>
              ) : (
                <div></div>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  );
};

export default NewUserForm;